import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Border from "../../assets/images/BorderColor.png";
import IvoryList from "../../assets/icons/IvoryLogo.svg";
import getToken from "../../hooks/getToken";
import { Camera } from "lucide-react";
import { useUserData } from "../../store/UserData";
import ProfileLoading from "../loading/profile";
import Swal from "sweetalert2";
import ProfileImg from "../../assets/used-images/default.jpg";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

const EditProfile = () => {
    const { URL } = useLanguage();
    const token = getToken();
    const { t } = useTranslation();
    const { profile, isErr, isLoading ,refetch } = useUserData();

    const [state, setState] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        nationalId: "",
        address: "",
        oldPassword: "",
        company_title: "", // العنوان
        job_title: "",

        newPassword: "",
        newPasswordConfirm: "",
        image: "",  
    });

    const [img, setImg] = useState(null);
    const imageUploaderRef = useRef();
    const formData = new FormData();
    const readerForImg = new FileReader();

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (profile) {
            setState({
                firstName: profile?.first_name || "",
                lastName: profile?.last_name || "",
                email: profile?.email || "",
                phone: profile?.phone || "",
                nationalId: profile["national ID"] || "",
                address: profile?.address || "",  // If address is null, use an empty string
                company_title: profile?.company_title || "", 
                job_title: profile?.job_title || "", 
                image: profile?.image || "",
            });
        }
    }, [profile]);
    if (isErr) return <div className="h-dvh w-full flex justify-center items-center"><p>{isErr}</p></div>;
    if (isLoading) return <ProfileLoading editProfile={true} />;

    const handleChange = (e) => {
        setState({ ...state, [e.target.name]: e.target.value });
    };

    async function updateProfileData(e) {
        e.preventDefault();
        setIsSubmitting(true);
        formData.append('first_name', state.firstName);
        formData.append('last_name', state.lastName);
        formData.append('email', state.email);
        formData.append('national_id', state.nationalId);
        formData.append('phone', state.phone);
        formData.append('address', state.address);
        formData.append('company_title', state.company_title); // إضافة العنوان
        formData.append('job_title', state.job_title);
        if (state.image instanceof File) {
            formData.append('image', state.image);
        }
    
        try {
            const response = await axios.post(`${URL}/profile`, formData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                }
            });
    
            const updatedData = response.data;
            if (updatedData && updatedData.company && updatedData.company.image) {
                setState(prevState => ({
                    ...prevState,
                    image: updatedData.company.image
                }));
            }
    
            Swal.fire({
                title: t('custom.done'),
                icon: "success",
                confirmButtonText: t('custom.accept')
            }).then(() => {
                // Reload the page when OK is clicked
                window.location.reload();
            });
    
            refetch();
    
        } catch (err) {
            console.error("Error updating profile:", err);
    
            Swal.fire({
                title: t('custom.err-title'),
                text: t('custom.err'),
                icon: "error",
                confirmButtonText: t('custom.accept')
            });
        } finally {
            setIsSubmitting(false); 
        }
    }
    
    return (
        <>
            <section className="border p-3 rounded-xl">
                {/* Profile Image */}
                <div className="flex justify-center">
                    <div className='rounded-full relative p-1' onClick={() => imageUploaderRef.current.click()}>
                        <img src={Border} alt="border" className='w-40 absolute top-0 right-0' />
                        {/* Display image from state or use default image */}
                        <img
                            src={img || state.image || ProfileImg}
                            alt="profile-img"
                            className='rounded-full size-32 m-2 object-center'
                        />
                        <button className="absolute bottom-3 right-5">
                            <input type="file" accept="image/*" className="hidden" ref={imageUploaderRef}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    setState({ ...state, image: file });  
                                    formData.append('image', file);
                                    readerForImg.onload = (e) => {
                                        setImg(e.target.result); 
                                    };
                                    readerForImg.readAsDataURL(file);
                                }}
                            />
                            <Camera size={30} color="white" fill="var(--primary-color)" />
                        </button>
                    </div>
                </div>

                {/* Profile Form */}
                <form onSubmit={updateProfileData}>
                    <div className="grid grid-cols-2 gap-2 max-lg:grid-cols-1">
                        {[
                            { label: t('contact.first-name'), name: 'firstName', type: 'text' },
                            { label: t('contact.second-name'), name: 'lastName', type: 'text' },
                            { label: t('contact.email'), name: 'email', type: 'email' },
                            { label: t('contact.phone'), name: 'phone', type: 'number' },
                            { label: t('contact.nationalId'), name: 'nationalId', type: 'number' },
                            { label: t('contact.address-2'), name: 'address', type: 'text' },
                            { label: t('contact.company-title'), name: 'company_title', type: 'text' }, // حقل العنوان
                            { label: t('contact.job-title'), name: 'job_title', type: 'text' }, // حقل الوظيفة
                        ].map((input, i) => (
                            <div key={i}>
                                <span className="flex items-center gap-2 mb-2">
                                    <img src={IvoryList} alt="list" />
                                    <label htmlFor={input.name} className="text-secondary-tw">
                                        {input.label}
                                    </label>
                                </span>
                                <input
                                    type={input.type}
                                    id={input.name}
                                    name={input.name}
                                    value={state[input.name]||""}
                                    onChange={handleChange}
                                    className="select-ui"
                                    autoComplete="on"
                                />
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end w-full mt-4">
                        <button type="submit" className="bg-primary-tw py-1 px-10 text-center text-white rounded-lg" disabled={isSubmitting}>
                            {isSubmitting ? t('custom.updating') : t('custom.update')}
                        </button>
                    </div>
                </form>
            </section>
        </>
    );
};

export default EditProfile;
